import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  ArrowLeft,
  RefreshCcw,
  User,
  Users,
  Baby,
  Briefcase,
  Wallet,
  PieChart,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Download,
  ChevronRight,
} from "lucide-react";
import { formatCurrency, formatNumber, getInitials } from "../lib/utils";
import { toTitleCase } from "../lib/formatName";
import { Avatar, AvatarFallback } from "../components/ui/avatar";

const ClientDetail = () => {
  const { clientId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [client, setClient] = useState(null);
  const [subAccounts, setSubAccounts] = useState([]);
  const [allTotals, setAllTotals] = useState(null);
  const [selectedSubAccount, setSelectedSubAccount] = useState("all");
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [holdingsLoading, setHoldingsLoading] = useState(false);

  useEffect(() => {
    fetchClientData();
  }, [clientId]);

  useEffect(() => {
    if (client) {
      fetchHoldings();
    }
  }, [selectedSubAccount, client]);

  const fetchClientData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/clients/${clientId}/sub-accounts`);
      setClient(response.data.client);
      setSubAccounts(response.data.sub_accounts);
      setAllTotals(response.data.all_totals);
    } catch (error) {
      toast.error("Failed to load client data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHoldings = async () => {
    setHoldingsLoading(true);
    try {
      const url = selectedSubAccount === "all" 
        ? `/clients/${clientId}/holdings`
        : `/clients/${clientId}/holdings?sub_account=${selectedSubAccount}`;
      const response = await api.get(url);
      setHoldings(response.data.holdings);
    } catch (error) {
      toast.error("Failed to load holdings");
    } finally {
      setHoldingsLoading(false);
    }
  };

  const getSubAccountIcon = (type) => {
    switch (type) {
      case "self":
        return <User className="w-4 h-4" />;
      case "self_minor":
        return <Baby className="w-4 h-4" />;
      case "joint":
        return <Users className="w-4 h-4" />;
      case "as_joint":
        return <Users className="w-4 h-4" />;
      case "minor":
        return <Baby className="w-4 h-4" />;
      default:
        return <Briefcase className="w-4 h-4" />;
    }
  };

  const getSubAccountColor = (type, isSelected) => {
    if (!isSelected) return "bg-gray-100 text-gray-700 hover:bg-gray-200";
    
    switch (type) {
      case "self":
        return "bg-teal-600 text-white";
      case "self_minor":
        return "bg-orange-500 text-white";
      case "joint":
        return "bg-blue-600 text-white";
      case "as_joint":
        return "bg-indigo-600 text-white";
      case "minor":
        return "bg-purple-600 text-white";
      default:
        return "bg-gray-600 text-white";
    }
  };

  const getSelectedSubAccountData = () => {
    if (selectedSubAccount === "all") {
      return {
        label: "All Holdings",
        total_invested: allTotals?.total_invested || 0,
        total_units: allTotals?.total_units || 0,
        folio_count: allTotals?.folio_count || 0
      };
    }
    return subAccounts.find(s => s.key === selectedSubAccount) || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="client-detail-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Client not found</p>
        <Button onClick={() => navigate("/clients")} className="mt-4">
          Back to Clients
        </Button>
      </div>
    );
  }

  const selectedData = getSelectedSubAccountData();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/clients")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-gray-800">Client Profile</h1>
          <p className="text-gray-500">View holdings and sub-accounts</p>
        </div>
        <Button variant="outline" onClick={fetchClientData}>
          <RefreshCcw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
        <Button variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
      </div>

      {/* Client Info Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar & Basic Info */}
            <div className="flex items-center gap-4">
              <Avatar className="w-20 h-20">
                <AvatarFallback 
                  className="text-2xl text-white font-semibold"
                  style={{ backgroundColor: "#2d6b62" }}
                >
                  {getInitials(client.name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">{client.name}</h2>
                <p className="text-gray-500 font-mono">{client.pan_no}</p>
                <Badge 
                  className={client.is_active ? "bg-green-100 text-green-800 mt-1" : "bg-red-100 text-red-800 mt-1"}
                >
                  {client.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>

            {/* Contact Info */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
              {client.email && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm truncate">{client.email}</span>
                </div>
              )}
              {client.phone && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4" />
                  <span className="text-sm">{client.phone}</span>
                </div>
              )}
              {client.city && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm">{client.city}</span>
                </div>
              )}
            </div>

            {/* Summary Stats */}
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-teal-700">
                  {formatCurrency(allTotals?.total_invested || 0)}
                </p>
                <p className="text-sm text-gray-500">Total Invested</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-700">
                  {allTotals?.folio_count || 0}
                </p>
                <p className="text-sm text-gray-500">Folios</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-700">
                  {subAccounts.length}
                </p>
                <p className="text-sm text-gray-500">Sub-Accounts</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sub-Account Capsules */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sub-Accounts</CardTitle>
          <CardDescription>
            Select a sub-account to view specific holdings or "All" for consolidated view
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {/* All Button */}
            <button
              onClick={() => setSelectedSubAccount("all")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedSubAccount === "all"
                  ? "bg-gray-800 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              data-testid="sub-account-all"
            >
              <Briefcase className="w-4 h-4" />
              <span>All</span>
              <Badge variant="secondary" className="ml-1 bg-white/20">
                {allTotals?.folio_count || 0}
              </Badge>
            </button>

            {/* Sub-Account Capsules */}
            {subAccounts.map((sub) => (
              <button
                key={sub.key}
                onClick={() => setSelectedSubAccount(sub.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  getSubAccountColor(sub.type, selectedSubAccount === sub.key)
                }`}
                data-testid={`sub-account-${sub.key}`}
              >
                {getSubAccountIcon(sub.type)}
                <span className="max-w-[200px] truncate">{sub.label}</span>
                <Badge variant="secondary" className="ml-1 bg-white/20 text-inherit">
                  {sub.folio_count}
                </Badge>
              </button>
            ))}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-teal-600" />
              <span>Self (Single)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-blue-600" />
              <span>Joint Holder</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-indigo-600" />
              <span>As Joint (2nd/3rd)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-purple-600" />
              <span>Minor (as Guardian)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-orange-500" />
              <span>Self (Minor)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selected Sub-Account Summary */}
      {selectedData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-teal-600 font-medium">
                    {selectedSubAccount === "all" ? "Total Invested" : `${selectedData.label} - Invested`}
                  </p>
                  <p className="text-2xl font-bold text-teal-800">
                    {formatCurrency(selectedData.total_invested)}
                  </p>
                </div>
                <Wallet className="w-8 h-8 text-teal-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total Units</p>
                  <p className="text-2xl font-bold text-blue-800">
                    {formatNumber(selectedData.total_units)}
                  </p>
                </div>
                <PieChart className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Folios</p>
                  <p className="text-2xl font-bold text-purple-800">
                    {selectedData.folio_count}
                  </p>
                </div>
                <CreditCard className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium">Schemes</p>
                  <p className="text-2xl font-bold text-orange-800">
                    {selectedData.scheme_count || holdings.length}
                  </p>
                </div>
                <Briefcase className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Holdings Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Holdings {selectedSubAccount !== "all" && `- ${selectedData?.label}`}
          </CardTitle>
          <CardDescription>
            {holdings.length} holdings in this {selectedSubAccount === "all" ? "account" : "sub-account"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {holdingsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
            </div>
          ) : holdings.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No holdings found for this sub-account
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Folio No</TableHead>
                    <TableHead>Scheme</TableHead>
                    <TableHead>Holding Pattern</TableHead>
                    <TableHead className="text-right">Units</TableHead>
                    <TableHead className="text-right">Invested</TableHead>
                    <TableHead>Option</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {holdings.slice(0, 50).map((holding, idx) => (
                    <TableRow key={idx} data-testid={`holding-row-${idx}`}>
                      <TableCell className="font-mono text-sm">{holding.folio_no}</TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          <p className="font-medium text-gray-800 truncate">{holding.scheme_name}</p>
                          <p className="text-sm text-gray-500">{holding.amc_code}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{toTitleCase(holding.investor_name)}</p>
                          {holding.joint1_name && (
                            <p className="text-sm text-blue-600">+ {holding.joint1_name}</p>
                          )}
                          {holding.joint2_name && (
                            <p className="text-sm text-indigo-600">+ {holding.joint2_name}</p>
                          )}
                          {holding.guard_name && (
                            <p className="text-sm text-purple-600">Guardian: {holding.guard_name}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatNumber(holding.units)}
                      </TableCell>
                      <TableCell className="text-right font-medium text-teal-700">
                        {formatCurrency(holding.invested)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-sm">
                          {holding.reinvest_flag === "Z" ? "Growth" : 
                           holding.reinvest_flag === "Y" ? "Reinvest" : 
                           holding.reinvest_flag === "N" ? "Payout" : 
                           holding.reinvest_flag || "N/A"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {holdings.length > 50 && (
                <p className="text-center text-sm text-gray-500 mt-4">
                  Showing 50 of {holdings.length} holdings
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientDetail;
